import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Box, FieldSelect, FormField, FormGrid, HelperField, ModalBody, ModalFoot, ModalHead, Tabs, Text, } from '@dixa/lib-shared-habla';
import classnames from 'classnames';
import { HTTPRequestMethod } from 'lib-shared-legacy-types';
import noop from 'lodash/noop';
import { useCallback, useState } from 'react';
import { Field, FieldArray, Form } from 'redux-form';
import { v4 as uuid } from 'uuid';
import { ajaxSend } from 'api/api';
import { getConfirmButtonVerb } from 'components/powerFlowShared/helpers/getConfirmButtonVerb';
import { FieldAnnouncementPicker } from 'components/shared/announcementPicker';
import { AdapterTypes } from 'components/shared/announcementPicker/adapters';
import AuthGeneratorComponent from 'components/shared/authGeneratorComponent/AuthGeneratorComponent';
import { FieldRepeater, RepeaterDivider } from 'fable/fieldRepeater';
import { FlowType, RequestSimulator, } from 'fable/flowEditor';
import { TokenInput } from 'fable/tokenInput';
import { getAvailableFlowVariables, } from '../../helpers/flowVariableHelpers';
import { FlowNodeTypes } from '../types';
import styles from './integration.module.scss';
import { contentTypes, HTMLRequestTypes, tabs } from './integration/constants';
import { HeaderRow } from './integration/headerRow';
import { getStatusFromResponse, getVariables, parseJSONFromResponse, } from './integration/helpers';
import { VariableRow } from './integration/variableRow';
export const ANNOUNCEMENT_FIELD = 'announcement';
const BODY_FIELD = 'body';
const CONTENT_TYPE_FIELD = 'contentType';
export const HEADERS_FIELD = 'headers';
const HTTP_REQUEST_TYPE_FIELD = 'httpRequestType';
export const URL_FIELD = 'url';
export const VARIABLES_FIELD = 'variables';
export function Integration({ flowType, formValues, handleSubmit, node, nodeLabel, payload, replaceNode, scopeVars, triggerClose, dirty: isChanged, }) {
    const isPhoneFlow = flowType === FlowType.FLOW_TYPE_CALL;
    const [test, setTest] = useState({
        loading: false,
        data: null,
        status: [
            {
                name: 'Status',
                text: 'No response.',
                color: 'DEFAULT',
            },
        ],
    });
    const [selectedTab, setSelectedTab] = useState('headers');
    const [hasValidAnnouncement, setHasValidAnnouncement] = useState(true);
    const handleTabSelection = useCallback((tabId) => {
        if (tabId) {
            setSelectedTab(tabId);
        }
    }, [setSelectedTab]);
    const getHeadersForPayload = useCallback((headers) => {
        return headers.reduce((payloadHeaders, { key, value }) => {
            payloadHeaders[key] = value;
            return payloadHeaders;
        }, {});
    }, []);
    const handleTestRequest = useCallback((values) => {
        if (formValues) {
            const { url, body, contentType, headers, httpRequestType } = formValues;
            setTest(Object.assign(Object.assign({}, test), { data: null, loading: true }));
            const scopeVarRegex = /{{(([^:]*:)?([^}]+))}}/g;
            const replaceFunc = (_fullMatch, key) => (values === null || values === void 0 ? void 0 : values[key])
                ? JSON.stringify(values === null || values === void 0 ? void 0 : values[key]).replace(/^"(.*)"$/, '$1')
                : '';
            const replacedBody = body.replace(scopeVarRegex, replaceFunc);
            const startTime = Date.now();
            ajaxSend({
                method: HTTPRequestMethod.Post,
                path: '/v1/flows/integration_node/url',
                data: {
                    url: url !== null && url !== void 0 ? url : '',
                    placeholder_to_value: values,
                },
            }, true)
                .then((response) => new Promise((resolve) => {
                var _a, _b;
                if ((_b = (_a = response.data) === null || _a === void 0 ? void 0 : _a.data) === null || _b === void 0 ? void 0 : _b.is_success) {
                    resolve(ajaxSend({
                        method: HTTPRequestMethod.Post,
                        path: '/v1/flows/integration_node/validate_http_call',
                        data: {
                            id: response.data.data.id,
                            http_request_type: httpRequestType,
                            body: replacedBody,
                            content_type: contentType,
                            headers: getHeadersForPayload(headers),
                        },
                    }, true));
                }
                else {
                    throw {
                        response: {
                            data: {
                                data: {
                                    body: 'Internal Dixa error',
                                },
                            },
                        },
                    };
                }
            }))
                .then((response) => {
                setTest({
                    data: parseJSONFromResponse(response) || null,
                    loading: false,
                    status: getStatusFromResponse(response, startTime),
                });
            })
                .catch(({ response }) => {
                setTest({
                    data: parseJSONFromResponse(response) || null,
                    loading: false,
                    status: getStatusFromResponse(response, startTime),
                });
            });
        }
    }, [formValues, getHeadersForPayload, setTest, test]);
    const isValidAnnouncement = useCallback((announcement) => {
        if (isPhoneFlow) {
            const file = (announcement === null || announcement === void 0 ? void 0 : announcement.file) || null;
            const isValid = !!file;
            if (isValid !== hasValidAnnouncement) {
                setHasValidAnnouncement(isValid);
            }
            return hasValidAnnouncement;
        }
        return true;
    }, [isPhoneFlow, hasValidAnnouncement, setHasValidAnnouncement]);
    const handleSaveClick = useCallback(({ announcement, body, contentType, headers, httpRequestType, url, variables, }) => {
        if (isValidAnnouncement(announcement)) {
            const headersForPayload = getHeadersForPayload(headers);
            const variablesForPayload = variables.map((variable) => (Object.assign(Object.assign({}, variable), { type: 'scope' })));
            const nodeOptions = Object.assign(Object.assign({}, node), { nodeType: FlowNodeTypes.INTEGRATION, text: 'Fetch data', icon: 'i-channels', isRemovable: true, isEditable: true, isAction: false, badges: [], overlay: [], payload: Object.assign({ body,
                    contentType, headers: headersForPayload, httpRequestType, nextNodeId: payload.nextNodeId || uuid(), url, variables: variablesForPayload }, (isPhoneFlow &&
                    announcement && {
                    announcement: {
                        file: Object.assign(Object.assign({}, announcement), { filename: announcement.label }),
                    },
                })) });
            replaceNode(nodeOptions);
            triggerClose();
        }
    }, [
        getHeadersForPayload,
        isPhoneFlow,
        isValidAnnouncement,
        node,
        payload,
        replaceNode,
        triggerClose,
    ]);
    const availableFlowVariables = getAvailableFlowVariables({
        flowType,
        scopeVars,
    });
    return (_jsxs(Box, Object.assign({ width: "780px" }, { children: [_jsx(ModalHead, { className: styles.head, onClose: triggerClose, title: nodeLabel }), _jsxs(Form, Object.assign({ onSubmit: handleSubmit(handleSaveClick) }, { children: [_jsxs(ModalBody, { children: [_jsx(Text.Paragraph, Object.assign({ subdued: true }, { children: "Integrate third-party systems or your own API to route conversations with the filter node. All conversations can be routed based on relevant customer information in Dixa (e.g. previous orders, tracking links, etc.)." })), _jsxs(FormGrid, Object.assign({ className: styles.form }, { children: [_jsx(FormField, Object.assign({ span: 1 }, { children: _jsx(Field, { component: FieldSelect, items: HTMLRequestTypes, label: "Method", name: HTTP_REQUEST_TYPE_FIELD }) })), _jsx(FormField, Object.assign({ span: 5 }, { children: _jsx(Field, { className: styles.tokenInput, component: TokenInput, hasNewStyle: true, label: "URL", name: URL_FIELD, placeholder: "Your api url", variables: availableFlowVariables }) })), _jsx(FormField, { children: _jsx(Tabs, { className: styles.tabs, onChange: handleTabSelection, selectedId: selectedTab, tabs: tabs }) }), _jsx(FormField, Object.assign({ className: classnames({ hide: selectedTab !== 'headers' }) }, { children: _jsx(FieldArray, { actions: AuthGeneratorComponent, buttonProps: {
                                                className: styles.addButton,
                                                label: 'Add header',
                                            }, className: styles.fieldRepeater, component: FieldRepeater, divider: _jsx(RepeaterDivider, { className: styles.divider }), style: { maxHeight: 238 }, name: HEADERS_FIELD, rowComponent: HeaderRow, title: "Headers" }) })), _jsxs(FormField, Object.assign({ className: classnames({ hide: selectedTab !== 'body' }) }, { children: [_jsx(Field, { component: FieldSelect, items: contentTypes, label: "Content-type", name: CONTENT_TYPE_FIELD }), _jsx(Field, { component: TokenInput, hasNewStyle: true, mode: "textarea", name: BODY_FIELD, noAlert: true, noLabel: true, placeholder: "Content", variables: availableFlowVariables })] })), _jsx(FormField, Object.assign({ className: classnames({ hide: selectedTab !== 'test' }) }, { children: _jsx(RequestSimulator, { data: test.data, helpText: "To check that everything was setup correctly and see test results, send a test request over HTTP/HTTPS.", isAligned: true, loading: test.loading, onChange: noop, onSend: handleTestRequest, status: test.status, title: "Test HTTP", variables: getVariables(availableFlowVariables, formValues) }) })), _jsxs(FormField, { children: [_jsx("h2", Object.assign({ className: styles.subTitle }, { children: "Input condition" })), _jsx("p", Object.assign({ className: styles.fieldDescription }, { children: "Make sure the variable names match the JSON-path in the JSON object in the response." })), _jsx(FieldArray, { buttonProps: {
                                                    className: styles.addButton,
                                                    label: 'Add variable',
                                                }, className: styles.fieldRepeater, component: FieldRepeater, divider: _jsx(RepeaterDivider, { className: styles.divider }), style: { maxHeight: 238 }, name: VARIABLES_FIELD, rowComponent: VariableRow, rowProps: { variablesValue: formValues === null || formValues === void 0 ? void 0 : formValues.variables }, title: "Variables" })] }), flowType === FlowType.FLOW_TYPE_CALL && (_jsxs(FormField, Object.assign({ className: styles.announcement }, { children: [_jsx(HelperField, Object.assign({ className: styles.announcementHeader, fieldId: ANNOUNCEMENT_FIELD, label: "Announcement" }, { children: "Fetching data can take a few seconds. Please choose an announcement to play while the data is being fetched." })), _jsx(Field, { adapter: AdapterTypes.Announcement, component: FieldAnnouncementPicker, name: ANNOUNCEMENT_FIELD, onSelect: noop })] })))] }))] }), _jsx(ModalFoot, { cancelOnClick: triggerClose, confirmName: getConfirmButtonVerb(node), confirmProps: { disabled: !isChanged }, confirmOnClick: handleSubmit(handleSaveClick) })] }))] })));
}
